/**
 * chat-actions.ts
 * 聊天动作工具集（native tool calling）
 *
 * 聊天动作工具按 isTerminal 区分两类：
 * - 终止工具执行后直接返回，不将结果回送给 LLM，不触发第二轮请求。
 * - 查询工具执行后将结果回送给 LLM，触发第二轮请求继续生成回复。
 *
 * 架构说明：
 * - send_messages：主消息输出，execute 调用 dispatchMessages 驱动假打字机动画
 * - 副作用工具：复用 chat-post-processors.ts 里已有的 handler 函数
 * - 复杂交互（通话/拉黑/好友申请/挂断）：通过回调委托给 chat.ts 处理，保持关注分离
 *
 * 旧格式结构化 toolbox 仅作为 fallback（chat.ts 的 processAIResponse），不参与正常请求。
 */

import type { ParsedMessageItem } from '../stores/chat-message-dispatcher'
import type { PostProcessContext } from '../stores/chat-post-processors'
import type { Post, PostComment } from '../stores/posts'
import type { ConversationSummary, MemoryItem } from '../types/memory'
import type { RelationshipEvent } from '../types/relationship'
import type { ScheduledTaskSpec } from '../types/scheduled-task'
import type { ToolLifecycle } from './tool-lifecycle'

import {
  handleOfflineModeSwitch,
  handlePluginActions,
  handlePostCreation,
  handleRecallMessage,
  handleRelationshipAdjustment,
  handleReminder,
  handleScheduledTask,
  handleStickerSend,
  handleVoiceMessageSend,
} from '../stores/chat-post-processors'
import { getToolLifecycle, ToolInputError } from './tool-lifecycle'

// ============================================================
// 类型定义
// ============================================================

/**
 * 群聊单条消息（group_chat_response 工具参数项）。
 * 字段与旧 JSON 数组格式一一对应，便于直接喂给 parseMessagesArray。
 */
export interface GroupChatMessageItem {
  senderId: string
  content?: string
  type?: 'text' | 'sticker' | 'pat' | 'reply'
  /** type=sticker 时的表情包名称 */
  name?: string
  /** type=reply 时被回复者的昵称 */
  replyTo?: string
  /** type=reply 时被回复消息的摘要 */
  replyContent?: string
}

/** 聊天动作工具接口：可以是终止工具或非终止工具 */
export interface ChatActionTool {
  function: {
    name: string
    description: string
    parameters: Record<string, unknown>
  }
  /** 执行工具，返回结果字符串（终止工具返回空字符串，非终止工具返回数据给 LLM） */
  execute: (args: unknown) => Promise<string>
  /** 新生命周期字段；未填写时按工具名称和旧 isTerminal 自动推导。 */
  lifecycle?: ToolLifecycle
  /** 标记为终止工具（true）或非终止工具（false）。终止工具执行后不回送结果给 LLM，非终止工具会继续 LLM 循环 */
  isTerminal: boolean
}

/** 返回真正的终止聊天工具名称，查询工具必须保留在后续 LLM 工具循环中。 */
export function getTerminalChatActionToolNames(tools: ChatActionTool[]): Set<string> {
  return new Set(tools.filter(tool => getToolLifecycle(tool) === 'terminal').map(tool => tool.function.name))
}

/**
 * 工具构建所需的上下文。
 *
 * 由 chat.ts 在每次 LLM 调用前构造并传入 buildChatActionTools()。
 * 复杂交互通过回调委托给 chat.ts 处理。
 */
export interface ChatToolContext {
  /** send_messages 工具：将消息列表分发到对话（含假打字机动画）；innerVoice 为本轮内心独白 */
  dispatchMessages: (items: ParsedMessageItem[], innerVoice?: string) => Promise<void>
  /** 副作用工具：记忆/贴纸/提醒/朋友圈/关系/撤回/插件 */
  postProcessCtx: PostProcessContext
  /** 当前对话的联系人 ID（pat/拉黑 需要） */
  targetContactId: string | null
  /** 当前会话允许 AI 切换到的线下模式状态；未提供时不向模型暴露该能力 */
  allowedOfflineModeSwitch?: 'offline' | 'online'
  /** 当前电话交互状态，用于只暴露此时可执行的通话工具 */
  callState?: 'idle' | 'incoming' | 'outgoing' | 'active'
  /** 单聊/群聊区分回复协议。 */
  isGroupChat?: boolean
  /** 当前由应用状态确认的专用决策任务。 */
  taskKind?: 'friend-request-decision' | 'post-comment'
  /** 任一终止工具执行完成后通知聊天收尾逻辑，避免误报空响应。 */
  onTerminalToolHandled?: () => void
  /** 普通动作完成后通知聊天收尾逻辑，避免动作已生效却被误判为空响应。 */
  onActionToolHandled?: () => void
  /** 单轮工具预算耗尽时显示一次系统提示。 */
  onToolBudgetExceeded?: () => void
  // --- 委托给 chat.ts 处理的复杂动作 ---
  /** 发起通话（voice/video） */
  onCallInitiate: (type: 'voice' | 'video', greeting: string) => void
  /** AI 挂断通话 */
  onHangUp: (reason?: string) => void
  /** AI 回应好友申请 */
  onFriendRequestResponse: (decision: 'accept' | 'reject', message: string) => void
  /** AI 拉黑用户 */
  onBlockUser: (reason: string) => void
  /** AI 在通话中说话（终止工具，替代 JSON dialogue） */
  onSpeakInCall: (dialogue: string, description?: string) => void
  /** AI 接听/拒绝来电（终止工具，替代通话请求 JSON 决策） */
  onAnswerCall: (decision: 'accept' | 'reject', message: string) => void
  // --- 群聊专用（仅群聊会话下由 chat.ts 注入；缺席时 group_chat_response 工具不注册） ---
  /** 群聊消息分发：把 group_chat_response 的参数交回 chat.ts 走 parseMessagesArray + 假打字机 */
  dispatchGroupMessages?: (groupMessages: GroupChatMessageItem[]) => Promise<void>
  /** 群成员列表，用于给 senderId 生成 enum 白名单 */
  groupMembers?: Array<{ id: string, name: string }>
}

/**
 * 所有聊天动作工具的名称集合。
 *
 * 用途：UI 层据此判断某个 tool-call 是否为「内部聊天动作」——
 * 这些工具（发消息/记忆/关系等）在旧 JSON 格式下本就无工具调用气泡，
 * 迁移到 native tool 后也不应显示「正在使用工具」提示；只有真正的 MCP/调试工具才显示。
 */
export const CHAT_ACTION_TOOL_NAMES: ReadonlySet<string> = new Set([
  'send_messages',
  'send_images',
  'silent',
  'create_memory',
  'send_sticker',
  'pat',
  'create_post',
  'like_post',
  'comment_on_post',
  'recall_message',
  'set_reminder',
  'schedule_task',
  'initiate_voice_call',
  'initiate_video_call',
  'hang_up_call',
  'speak_in_call',
  'answer_call',
  'block_user',
  'friend_request_response',
  'update_relationship',
  'plugin_action',
  // 朋友圈后台模拟专用工具
  'create_moments_post',
  'like_moments_post',
  'comment_on_moments_post',
  'do_nothing_moments',
  // 群聊专用工具
  'group_chat_response',
  // 关系管理工具
  'respond_to_unblock_opportunity',
  // 记忆摘要工具
  'submit_conversation_summary',
  // 注意：search_memory, get_recent_posts, get_relationship_history 不在此列表中
  // 这些是真正的"查询工具"，应该显示工具调用气泡，让用户知道 AI 在查询数据
])

const TOOL_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  list_mcp_servers: '查看外部服务',
  load_mcp_tools: '加载外部服务能力',
  call_mcp_tool: '调用外部服务',
  configure_mcp: '配置外部服务',
  remove_mcp_server: '移除外部服务',
  search_memory: '查询记忆',
  get_recent_posts: '查看朋友圈',
  get_relationship_history: '查看关系记录',
}

export function getToolDisplayName(toolName: string | undefined): string {
  if (!toolName)
    return '调用工具'
  return TOOL_DISPLAY_NAMES[toolName] ?? `调用工具：${toolName}`
}

// ============================================================
// 工具 Schema 常量
// ============================================================

const EMOTION_ENUM = ['normal', 'happy', 'sad', 'angry', 'surprised', 'shy', 'thinking', 'loving', 'cool'] as const

const SCHEMA_SEND_IMAGES: Record<string, unknown> = {
  type: 'object',
  properties: {
    images: {
      type: 'array',
      minItems: 1,
      maxItems: 9,
      description: '要发送的模拟图片，最多9张；只填写画面描述，不要填写真实图片 URL。',
      items: {
        type: 'object',
        properties: {
          description: { type: 'string', minLength: 1, maxLength: 2000, description: '图片画面描述，用户会看到这段模拟图片内容' },
        },
        required: ['description'],
      },
    },
  },
  required: ['images'],
}

function buildSendMessagesSchema(allowedOfflineModeSwitch?: 'offline' | 'online'): Record<string, unknown> {
  return {
    type: 'object',
    properties: {
      messages: {
        type: 'array',
        description: '要发送的消息列表，每条消息独立发出，之间有自然停顿',
        items: {
          type: 'object',
          properties: {
            text: { type: 'string', minLength: 1, description: '消息正文' },
            emotion: {
              type: 'string',
              enum: EMOTION_ENUM,
              description: '当前消息的情绪状态',
            },
            replyTo: { type: 'string', description: '被回复者的昵称（可选）；引用用户消息时填"你"' },
            replyContent: { type: 'string', description: '被回复消息的摘要（可选，与 replyTo 配合使用）' },
            voice: { type: 'boolean', description: '是否以语音消息形式发送（可选）' },
          },
          required: ['text'],
        },
        minItems: 1,
      },
      inner_voice: {
        type: 'string',
        maxLength: 400,
        description: '本轮回复时你的内心独白（可选）：用第一人称写你此刻真实的想法与感受，可以和说出口的话不一致。只写角色的心理活动，不要写分析、计划或任何跳出角色的内容。400字以内。',
      },
      relationship: {
        type: 'object',
        description: '本轮对话对好感度的影响（可选）。发消息时若关系有变化，在此填写，不要再单独调用 update_relationship。',
        properties: {
          score_change: { type: 'number', minimum: -10, maximum: 10, description: '好感度变化值（-10~10）' },
          reason: { type: 'string', maxLength: 500, description: '调整原因' },
          new_type: { type: 'string', maxLength: 20, description: '新关系类型（可选，仅在关系质变时填写）' },
        },
        required: ['score_change', 'reason'],
      },
      ...(allowedOfflineModeSwitch
        ? {
            offline_mode_switch: {
              type: 'string',
              enum: [allowedOfflineModeSwitch],
              description: allowedOfflineModeSwitch === 'offline'
                ? '可选：在发送本轮线上消息后切换到线下见面模式'
                : '可选：在发送本轮线下告别消息后切回线上聊天模式',
            },
          }
        : {}),
    },
    required: ['messages'],
  }
}

const SCHEMA_SILENT: Record<string, unknown> = {
  type: 'object',
  properties: {},
  description: '不发送任何消息，保持沉默',
}

const SCHEMA_CREATE_MEMORY: Record<string, unknown> = {
  type: 'object',
  properties: {
    content: { type: 'string', minLength: 1, maxLength: 2000, description: '要记住的内容' },
    keywords: { type: 'array', maxItems: 20, items: { type: 'string', minLength: 1 }, description: '相关关键词' },
    isImportant: { type: 'boolean', description: '是否标记为重要记忆' },
  },
  required: ['content', 'keywords', 'isImportant'],
}

const SCHEMA_SEARCH_MEMORY: Record<string, unknown> = {
  type: 'object',
  properties: {
    query: { type: 'string', description: '搜索关键词或问题，用于检索相关记忆' },
  },
  required: ['query'],
}

const SCHEMA_GET_RECENT_POSTS: Record<string, unknown> = {
  type: 'object',
  properties: {
    limit: { type: 'number', description: '返回的动态数量（默认10条，最多20条）', minimum: 1, maximum: 20 },
    authorId: { type: 'string', description: '筛选特定作者的动态（可选）' },
  },
}

const SCHEMA_GET_RELATIONSHIP_HISTORY: Record<string, unknown> = {
  type: 'object',
  properties: {
    characterId: { type: 'string', description: '要查询的角色ID（可选，不填则查询当前对话对象）' },
    eventLimit: { type: 'number', description: '返回的历史事件数量（默认10条，最多50条）', minimum: 1, maximum: 50 },
  },
}

const SCHEMA_SEND_STICKER: Record<string, unknown> = {
  type: 'object',
  properties: {
    name: { type: 'string', description: '表情包名称，必须与可用列表中的名称完全匹配' },
  },
  required: ['name'],
}

const SCHEMA_PAT: Record<string, unknown> = {
  type: 'object',
  properties: {
    senderId: { type: 'string', description: '群聊中执行拍一拍的成员 ID（单聊可省略）' },
  },
}

const SCHEMA_CREATE_POST: Record<string, unknown> = {
  type: 'object',
  properties: {
    postType: { type: 'string', enum: ['text', 'image'], description: '动态类型' },
    content: { type: 'string', minLength: 1, maxLength: 2000, description: '动态正文' },
    imageDescription: { type: 'string', minLength: 1, maxLength: 2000, description: '图片描述（图片动态时必填）' },
  },
  required: ['postType', 'content'],
}

const SCHEMA_LIKE_POST: Record<string, unknown> = {
  type: 'object',
  properties: {
    postId: { type: 'string', description: '要点赞的动态 ID（必须是上下文中见到的真实 ID）' },
  },
  required: ['postId'],
}

const SCHEMA_COMMENT_ON_POST: Record<string, unknown> = {
  type: 'object',
  properties: {
    postId: { type: 'string', description: '要评论的动态 ID' },
    commentText: { type: 'string', minLength: 1, maxLength: 2000, description: '评论内容' },
  },
  required: ['postId', 'commentText'],
}

const SCHEMA_RECALL_MESSAGE: Record<string, unknown> = {
  type: 'object',
  properties: {},
  description: '撤回最近一条自己发送的消息，2-5 秒后执行',
}

const SCHEMA_SET_REMINDER: Record<string, unknown> = {
  type: 'object',
  properties: {
    delay: { type: 'number', minimum: 0.001, maximum: 31536000, description: '提醒延迟秒数（最多一年）' },
    text: { type: 'string', minLength: 1, maxLength: 2000, description: '提醒内容' },
  },
  required: ['delay', 'text'],
}

const SCHEMA_SCHEDULE_TASK: Record<string, unknown> = {
  type: 'object',
  properties: {
    scheduleType: { type: 'string', enum: ['once', 'daily', 'weekly'], description: '一次性、每天或每周重复' },
    delaySeconds: { type: 'number', minimum: 0.001, maximum: 31622400, description: 'once 使用：从现在起延迟多少秒' },
    runAt: { type: 'string', description: 'once 使用：本地时间 YYYY-MM-DD HH:mm' },
    localTime: { type: 'string', description: 'daily/weekly 使用：本地时间 HH:mm' },
    weekday: { type: 'integer', minimum: 0, maximum: 6, description: 'weekly 使用：星期日=0，星期一=1，...星期六=6' },
    instruction: { type: 'string', minLength: 1, maxLength: 2000, description: '到点时要完成的任务意图；每次会结合最新上下文重新生成消息' },
  },
  required: ['scheduleType', 'instruction'],
}

const SCHEMA_INITIATE_VOICE_CALL: Record<string, unknown> = {
  type: 'object',
  properties: {
    greeting: { type: 'string', description: '接通后说的第一句话' },
  },
  required: ['greeting'],
}

const SCHEMA_INITIATE_VIDEO_CALL: Record<string, unknown> = {
  type: 'object',
  properties: {
    greeting: { type: 'string', description: '接通后说的第一句话' },
  },
  required: ['greeting'],
}

const SCHEMA_BLOCK_USER: Record<string, unknown> = {
  type: 'object',
  properties: {
    reason: { type: 'string', description: '拉黑原因（内部记录，不发给用户，写总结性原因）' },
  },
  required: ['reason'],
}

const SCHEMA_FRIEND_REQUEST_RESPONSE: Record<string, unknown> = {
  type: 'object',
  properties: {
    decision: { type: 'string', enum: ['accept', 'reject'], description: '接受或拒绝好友申请' },
    message: { type: 'string', description: '直接发送给用户的话' },
  },
  required: ['decision', 'message'],
}

const SCHEMA_HANG_UP_CALL: Record<string, unknown> = {
  type: 'object',
  properties: {
    reason: { type: 'string', description: '挂断前的最后一句话（可选）' },
  },
}

const SCHEMA_SPEAK_IN_CALL: Record<string, unknown> = {
  type: 'object',
  properties: {
    dialogue: { type: 'string', description: '你要说的话（极度口语化，可含停顿、语气词）' },
    description: { type: 'string', description: '视频通话时用第三人称描述表情/动作（可选，语音通话可省略）' },
  },
  required: ['dialogue'],
}

const SCHEMA_ANSWER_CALL: Record<string, unknown> = {
  type: 'object',
  properties: {
    decision: { type: 'string', enum: ['accept', 'reject'], description: '接受或拒绝来电' },
    message: { type: 'string', description: 'accept 时是接通后的第一句话；reject 时是拒绝理由' },
  },
  required: ['decision', 'message'],
}

const SCHEMA_UPDATE_RELATIONSHIP: Record<string, unknown> = {
  type: 'object',
  properties: {
    score_change: { type: 'number', minimum: -10, maximum: 10, description: '好感度变化值（-10~10）' },
    reason: { type: 'string', minLength: 1, maxLength: 500, description: '调整原因' },
    new_type: { type: 'string', maxLength: 20, description: '新关系类型（可选，仅在关系质变时填写）' },
  },
  required: ['score_change', 'reason'],
}

const SCHEMA_PLUGIN_ACTION: Record<string, unknown> = {
  type: 'object',
  properties: {
    pluginId: { type: 'string', description: '插件 ID' },
    action: { type: 'string', description: '插件动作名' },
    params: { type: 'object', description: '动作参数（可选）' },
  },
  required: ['pluginId', 'action'],
}

// ============================================================
// 工厂函数
// ============================================================

/**
 * 构建所有聊天动作工具列表。
 *
 * 在每次 LLM 调用前由 chat.ts 调用，传入当前会话上下文。
 * 返回的工具会被合并进 llm.ts 的 toolsList。
 *
 * @param ctx 当前会话上下文（含分发回调、后处理上下文、复杂动作委托回调）
 */
/**
 * 解析「当前正在行动的 AI 身份」的 contactId / contactName。
 * 优先取激活联系人，回落到角色卡，最后兜底为 default / AI。
 * like_post、comment_on_post 等朋友圈互动工具共用此逻辑，避免多处重复。
 */
function resolveActingContact(postProcessCtx: PostProcessContext): { contactId: string, contactName: string } {
  const { contactsStore, airiCardStore } = postProcessCtx
  const activeContact = postProcessCtx.targetContactId !== undefined
    ? (postProcessCtx.targetContactId ? contactsStore.getContact(postProcessCtx.targetContactId) : null)
    : contactsStore.activeContact
  return {
    contactId: activeContact?.id ?? airiCardStore.activeCardId ?? 'default',
    contactName: activeContact?.name ?? airiCardStore.characterName ?? 'AI',
  }
}

function isValidScheduledTaskArgs(task: Partial<ScheduledTaskSpec> | null | undefined): task is ScheduledTaskSpec {
  if (!task || (task.scheduleType !== 'once' && task.scheduleType !== 'daily' && task.scheduleType !== 'weekly'))
    return false
  if (typeof task.instruction !== 'string' || !task.instruction.trim() || task.instruction.trim().length > 2000)
    return false
  if (task.scheduleType === 'once') {
    const hasDelay = task.delaySeconds !== undefined
      && Number.isFinite(task.delaySeconds)
      && task.delaySeconds > 0
      && task.delaySeconds <= 366 * 86400
    const hasRunAt = typeof task.runAt === 'string'
      && /^\d{4}-\d{2}-\d{2}[T ](?:[01]\d|2[0-3]):[0-5]\d$/.test(task.runAt.trim())
    return hasDelay || hasRunAt
  }
  if (typeof task.localTime !== 'string' || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(task.localTime.trim()))
    return false
  if (task.scheduleType !== 'weekly')
    return true
  return typeof task.weekday === 'number' && Number.isInteger(task.weekday) && task.weekday >= 0 && task.weekday <= 6
}

export function buildChatActionTools(ctx: ChatToolContext): ChatActionTool[] {
  const { postProcessCtx, dispatchMessages, targetContactId, allowedOfflineModeSwitch, dispatchGroupMessages, groupMembers } = ctx

  // 本轮去重：send_messages.relationship 与独立 update_relationship 若同批出现，
  // 只应用一次关系调整（先应用者胜出），杜绝好感度双重计算。
  // 闭包变量随 buildChatActionTools 每轮新建，天然是「本轮」作用域，无需清理。
  let relationshipApplied = false
  async function applyRelationshipOnce(adjustment: { score_change: number, reason: string, new_type?: string }): Promise<boolean> {
    if (relationshipApplied) {
      console.warn('[relationship] 本轮已应用过关系调整，忽略重复调用以防双计:', adjustment.reason)
      return false
    }
    relationshipApplied = true
    return await handleRelationshipAdjustment({ text: '', relationshipAdjustment: adjustment }, postProcessCtx)
  }

  return [
    // ----------------------------------------------------------
    // 1. 消息输出
    // ----------------------------------------------------------
    {
      function: {
        name: 'send_messages',
        description: ctx.callState === 'active'
          ? '通话中优先使用 speak_in_call。如果调用本工具，消息会合并后作为通话语音说出。'
          : `向用户发送一条或多条消息。凡是需要对用户说话，都必须使用本工具，不要把可见回复直接放在 assistant content 中。每条消息独立发出，之间有自然停顿，支持 emotion 情绪标签；可用 inner_voice 附带本轮的内心独白。${allowedOfflineModeSwitch ? '当前还可通过 offline_mode_switch 随消息切换线上/线下模式。' : ''}`,
        parameters: buildSendMessagesSchema(allowedOfflineModeSwitch),
      },
      lifecycle: 'terminal',
      isTerminal: true,
      execute: async (args): Promise<string> => {
        const input = args as { messages?: unknown } | null
        if (!input || !Array.isArray(input.messages) || input.messages.length === 0)
          throw new ToolInputError('发送消息失败：messages 必须为非空数组。请使用 send_messages 重新提交至少一条含非空 text 的消息；保持沉默请使用 silent。')
        if (input.messages.some(message => !message || typeof message.text !== 'string' || !message.text.trim()))
          throw new ToolInputError('发送消息失败：每条消息都必须包含非空字符串 text。请修正后重新调用 send_messages。')
        let messageCount = 0
        let relationshipResult = ''
        try {
          const { messages, relationship, inner_voice: innerVoice, offline_mode_switch: offlineModeSwitch } = args as {
            messages: ParsedMessageItem[]
            inner_voice?: string
            relationship?: { score_change: number, reason: string, new_type?: string }
            offline_mode_switch?: 'offline' | 'online'
          }
          messageCount = messages.length
          if (ctx.callState === 'active') {
            const dialogue = messages.map(message => message.text?.trim()).filter(Boolean).join('\n')
            ctx.onSpeakInCall(dialogue)
          }
          else {
            // 若模型同轮同时输出正文和本工具，正文仍按现有兼容路径单独处理，可能产生两份消息。
            // native tool path 下 parsed.messages 为空，handleVoiceMessageSend 在 chat.ts
            // 里拿不到语音消息，需要在此处手动补调；用 forceRun 显式绕过 guard
            await dispatchMessages(messages, typeof innerVoice === 'string' ? innerVoice.trim().slice(0, 400) || undefined : undefined)
            await handleVoiceMessageSend({ forceRun: true }, postProcessCtx)
          }
          if (relationship) {
            if (!Number.isFinite(relationship.score_change) || relationship.score_change < -10 || relationship.score_change > 10 || typeof relationship.reason !== 'string' || !relationship.reason.trim() || relationship.reason.trim().length > 500 || (relationship.new_type !== undefined && (typeof relationship.new_type !== 'string' || relationship.new_type.trim().length > 20))) {
              relationshipResult = '关系调整失败：score_change 必须在 -10 到 10，reason 必须为 1-500 字符。'
            }
            else if (!await applyRelationshipOnce(relationship)) {
              relationshipResult = '关系调整失败：本轮关系调整已执行过或写入失败。'
            }
          }
          if (offlineModeSwitch) {
            if (offlineModeSwitch === allowedOfflineModeSwitch) {
              await handleOfflineModeSwitch({ text: '', offlineModeSwitch }, postProcessCtx)
            }
            else {
              console.warn(`[send_messages] 当前会话不允许切换到 ${offlineModeSwitch} 模式，已忽略`)
            }
          }
        }
        catch (error) {
          console.error('[send_messages] 消息分发失败:', error)
          throw error
        }
        const deliveryResult = ctx.callState === 'active'
          ? `已在通话中说出 ${messageCount} 条消息。`
          : `已发送 ${messageCount} 条消息。`
        return `${deliveryResult}${relationshipResult ? ` ${relationshipResult}` : ''}`
      },
    },
    {
      function: {
        name: 'silent',
        description: '不发送任何消息，保持沉默。当上下文不需要回应时使用。',
        parameters: SCHEMA_SILENT,
      },
      isTerminal: true,
      execute: async (): Promise<string> => '',
    },

    // ----------------------------------------------------------
    // 2. 记忆
    // ----------------------------------------------------------
    {
      function: {
        name: 'create_memory',
        description: '创建一条持久记忆，用于记住重要信息（用户偏好、约定、关键事件等）。',
        parameters: SCHEMA_CREATE_MEMORY,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { content, keywords, isImportant } = (args ?? {}) as {
          content: string
          keywords: string[]
          isImportant: boolean
        }
        if (typeof content !== 'string' || !content.trim() || content.trim().length > 2000 || typeof isImportant !== 'boolean' || (keywords != null && (!Array.isArray(keywords) || keywords.length > 20 || keywords.some(keyword => typeof keyword !== 'string' || !keyword.trim()))))
          return '创建记忆失败：content 必须为 1-2000 字符，keywords 必须为最多 20 个非空字符串。'
        if (postProcessCtx.targetContactId === null)
          return '创建记忆失败：当前没有可用的联系人上下文。'
        try {
          const created = postProcessCtx.memoryStore.createMemory(
            content.trim().slice(0, 2000),
            (keywords ?? []).slice(0, 20),
            Boolean(isImportant),
            undefined,
            postProcessCtx.targetContactId ? { contactId: postProcessCtx.targetContactId } : undefined,
          )
          if (!created)
            return '创建记忆失败：当前没有可用的联系人上下文。'
        }
        catch (error) {
          console.warn('[create_memory] 记忆创建失败:', error)
          return `创建记忆失败：${error instanceof Error ? error.message : String(error)}`
        }
        return '已创建记忆。'
      },
    },
    {
      function: {
        name: 'search_memory',
        description: '主动检索与给定查询相关的记忆。当你想回忆之前的对话内容、用户偏好或重要事件时使用。',
        parameters: SCHEMA_SEARCH_MEMORY,
      },
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { query } = (args ?? {}) as { query?: unknown }
        if (typeof query !== 'string' || !query.trim() || query.length > 500)
          return '记忆检索失败：query 必须为 1-500 字符的非空文本。'
        try {
          const normalizedQuery = query.trim()
          const result = postProcessCtx.memoryStore.retrieveRelevantMemories(
            normalizedQuery,
            postProcessCtx.targetContactId === undefined ? undefined : postProcessCtx.targetContactId,
          )

          // 格式化返回结果
          const summariesText = result.summaries.length > 0
            ? `对话摘要（${result.summaries.length}条）:\n${result.summaries.map((s: ConversationSummary) =>
              `- [${new Date(s.endTime).toLocaleDateString()}] ${s.summary}`,
            ).join('\n')}`
            : ''

          const memoriesText = result.memories.length > 0
            ? `记忆项（${result.memories.length}条）:\n${result.memories.map((m: MemoryItem) =>
              `- ${m.isImportant ? '⭐' : ''} ${m.content}${m.keywords.length > 0 ? ` [关键词: ${m.keywords.join(', ')}]` : ''}`,
            ).join('\n')}`
            : ''

          if (result.totalCount === 0) {
            return `未找到与"${normalizedQuery}"相关的记忆。`
          }

          return [summariesText, memoriesText].filter(Boolean).join('\n\n')
        }
        catch (error) {
          console.warn('[search_memory] 记忆检索失败:', error)
          return '记忆检索失败'
        }
      },
    },

    // ----------------------------------------------------------
    // 3. 表情包 / 拍一拍
    // ----------------------------------------------------------
    {
      function: {
        name: 'send_sticker',
        description: '发送一个表情包。name 必须与当前联系人可用的表情包名称完全匹配。',
        parameters: SCHEMA_SEND_STICKER,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { name } = (args ?? {}) as { name?: unknown }
        if (typeof name !== 'string' || !name.trim())
          return '发送表情包失败：name 必须为非空文本。'
        const success = await handleStickerSend({ text: '', sticker: { name } }, postProcessCtx)
        return success ? '已发送表情包。' : '发送表情包失败：未找到可用的表情包。'
      },
    },
    {
      function: {
        name: 'pat',
        description: '执行"拍一拍"动作。单聊可省略 senderId；群聊必须填写执行拍一拍的成员 ID。',
        parameters: SCHEMA_PAT,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { senderId } = (args ?? {}) as { senderId?: string }
        if (!targetContactId) {
          console.warn('[pat] 无法获取联系人 ID，已跳过')
          return '拍一拍失败：无法确定当前联系人。'
        }
        try {
          const { usePatInteraction } = await import('../composables/usePatInteraction')
          const { contactsStore } = postProcessCtx
          const conversationContact = contactsStore.getContact(targetContactId)
          const actorContact = conversationContact?.isGroup
            ? (senderId ? contactsStore.getContact(senderId) : undefined)
            : conversationContact
          if (!actorContact) {
            console.warn('[pat] 找不到执行拍一拍的联系人，已跳过')
            return '拍一拍失败：无法确定执行动作的成员。'
          }
          usePatInteraction().aiPatUser(targetContactId, actorContact.id, actorContact.name)
        }
        catch (error) {
          console.warn('[pat] 拍一拍执行失败:', error)
          return `拍一拍失败：${error instanceof Error ? error.message : String(error)}`
        }
        return '已执行拍一拍。'
      },
    },

    // ----------------------------------------------------------
    // 4. 朋友圈
    // ----------------------------------------------------------
    {
      function: {
        name: 'create_post',
        description: '在朋友圈发布一条动态。',
        parameters: SCHEMA_CREATE_POST,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { postType, content, imageDescription } = (args ?? {}) as {
          postType: 'text' | 'image'
          content: string
          imageDescription?: string
        }
        if ((postType !== 'text' && postType !== 'image') || typeof content !== 'string' || !content.trim() || content.trim().length > 2000 || (postType === 'image' && (typeof imageDescription !== 'string' || !imageDescription.trim() || imageDescription.trim().length > 2000)))
          return '发布动态失败：content 和 imageDescription 必须为不超过 2000 字符的文本。'
        const success = handlePostCreation({ text: '', post: { type: postType, content, imageDescription } }, postProcessCtx)
        return success ? '已发布朋友圈动态。' : '发布动态失败：朋友圈写入失败。'
      },
    },
    {
      function: {
        name: 'like_post',
        description: '点赞一条朋友圈动态。postId 必须是上下文中见到的真实 ID。',
        parameters: SCHEMA_LIKE_POST,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { postId } = (args ?? {}) as { postId?: unknown }
        if (typeof postId !== 'string' || !postId.trim())
          return '点赞失败：postId 必须为非空文本。'
        try {
          const { postsStore } = postProcessCtx
          const { contactId, contactName } = resolveActingContact(postProcessCtx)
          if (!postsStore.aiLikePost(postId, contactId, contactName))
            return '点赞失败：动态不存在或已经点过赞。'
        }
        catch (error) {
          console.warn('[like_post] 点赞失败:', error)
          return `点赞失败：${error instanceof Error ? error.message : String(error)}`
        }
        return '已点赞。'
      },
    },
    {
      function: {
        name: 'comment_on_post',
        description: '评论一条朋友圈动态。postId 必须是上下文中见到的真实 ID。',
        parameters: SCHEMA_COMMENT_ON_POST,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { postId, commentText } = (args ?? {}) as { postId?: unknown, commentText?: unknown }
        if (typeof postId !== 'string' || !postId.trim() || typeof commentText !== 'string' || !commentText.trim() || commentText.trim().length > 2000)
          return '评论失败：postId 必须为非空文本，commentText 必须为 1-2000 字符。'
        try {
          const { postsStore } = postProcessCtx
          const { contactId, contactName } = resolveActingContact(postProcessCtx)
          if (!postsStore.aiCommentOnPost(postId, commentText, contactId, contactName))
            return '评论失败：动态不存在。'
        }
        catch (error) {
          console.warn('[comment_on_post] 评论失败:', error)
          return `评论失败：${error instanceof Error ? error.message : String(error)}`
        }
        return '已发表评论。'
      },
    },
    {
      function: {
        name: 'get_recent_posts',
        description: '查看最近的朋友圈动态。可以筛选特定作者或查看所有人的动态。',
        parameters: SCHEMA_GET_RECENT_POSTS,
      },
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { limit = 10, authorId } = (args ?? {}) as { limit?: unknown, authorId?: unknown }
        if (typeof limit !== 'number' || !Number.isInteger(limit) || limit < 1 || limit > 20 || (authorId != null && (typeof authorId !== 'string' || !authorId.trim())))
          return '获取朋友圈动态失败：limit 必须为 1-20 的整数，authorId 必须为文本。'
        try {
          const { postsStore } = postProcessCtx
          let posts = postsStore.posts

          // 筛选作者
          if (authorId) {
            posts = posts.filter((p: Post) => p.authorId === authorId)
          }

          // 限制数量
          const limitedPosts = posts.slice(0, Math.min(limit, 20))

          if (limitedPosts.length === 0) {
            return authorId ? `没有找到该作者的动态。` : `朋友圈暂时没有动态。`
          }

          // 格式化输出
          const postsText = limitedPosts.map((post: Post) => {
            const author = post.authorId === 'user' ? '用户' : post.authorName
            const date = new Date(post.timestamp).toLocaleString('zh-CN')
            const likes = post.likes.length > 0 ? ` [${post.likes.length}个赞]` : ''
            const comments = post.comments.length > 0 ? ` [${post.comments.length}条评论]` : ''
            const type = post.type === 'image' ? '[图片]' : ''
            const mentions = post.mentionIds && post.mentionIds.length > 0 ? ' [@了某人]' : ''

            let result = `[${date}] ${author}${type}: ${post.content.substring(0, 100)}${post.content.length > 100 ? '...' : ''}${likes}${comments}${mentions}`

            // 如果有图片描述，添加上
            if (post.imageDescription) {
              result += `\n  图片描述: ${post.imageDescription}`
            }

            // 添加最近的评论
            if (post.comments.length > 0) {
              const recentComments = post.comments.slice(-3)
              result += `\n  最近评论: ${recentComments.map((c: PostComment) => `${c.author}: ${c.text}`).join('; ')}`
            }

            result += `\n  [postId: ${post.id}]`

            return result
          }).join('\n\n')

          return `最近的朋友圈动态（共${limitedPosts.length}条）:\n\n${postsText}`
        }
        catch (error) {
          console.warn('[get_recent_posts] 获取朋友圈失败:', error)
          return '获取朋友圈动态失败'
        }
      },
    },

    // ----------------------------------------------------------
    // 5. 撤回 / 沉默 / 提醒
    // ----------------------------------------------------------
    {
      function: {
        name: 'recall_message',
        description: '撤回最近一条自己发送的消息，会在 2-5 秒后执行。',
        parameters: SCHEMA_RECALL_MESSAGE,
      },
      isTerminal: true,
      execute: async (): Promise<string> => {
        handleRecallMessage({ text: '', recallTimestamp: -1 }, postProcessCtx)
        return ''
      },
    },
    {
      function: {
        name: 'set_reminder',
        description: '设置一个定时提醒，delay 秒后向用户发送提醒消息。',
        parameters: SCHEMA_SET_REMINDER,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { delay, text } = (args ?? {}) as { delay?: unknown, text?: unknown }
        if (typeof delay !== 'number' || !Number.isFinite(delay) || delay <= 0 || delay > 31_536_000 || typeof text !== 'string' || !text.trim() || text.trim().length > 2000)
          return '设置提醒失败：delay 必须为 1 到 31536000 秒，text 必须为 1-2000 字符。'
        const success = handleReminder({ text: '', delaySeconds: delay, reminderText: text }, postProcessCtx)
        return success ? '已设置提醒。' : '设置提醒失败：提醒参数无效。'
      },
    },
    {
      function: {
        name: 'schedule_task',
        description: '创建服务端持久化定时任务。到点后会根据最新人设、记忆和聊天上下文重新生成消息，支持一次性、每天或每周执行。',
        parameters: SCHEMA_SCHEDULE_TASK,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const task = args as Partial<ScheduledTaskSpec> | null
        if (!isValidScheduledTaskArgs(task))
          return '创建定时任务失败：时间、周期或 instruction 参数无效。'
        const success = await handleScheduledTask({ text: '', scheduledTask: task as ScheduledTaskSpec }, postProcessCtx)
        return success ? '已创建定时任务。' : '创建定时任务失败：当前会话不支持或服务端写入失败。'
      },
    },

    // ----------------------------------------------------------
    // 6. 通话
    // ----------------------------------------------------------
    {
      function: {
        name: 'initiate_voice_call',
        description: '主动发起语音通话，greeting 是接通后说的第一句话。',
        parameters: SCHEMA_INITIATE_VOICE_CALL,
      },
      isTerminal: true,
      execute: async (args): Promise<string> => {
        const { greeting } = args as { greeting: string }
        try {
          ctx.onCallInitiate('voice', greeting)
        }
        catch (error) {
          console.warn('[initiate_voice_call] 发起通话失败:', error)
        }
        return ''
      },
    },
    {
      function: {
        name: 'send_images',
        description: '向用户发送一张或多张模拟图片。用户要求发送、展示或分享图片时使用本工具；每张图片只写画面描述，不要使用真实图片 URL。执行后还可以继续调用其它动作。',
        parameters: SCHEMA_SEND_IMAGES,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        try {
          const rawImages = (args as { images?: unknown } | null)?.images
          if (!Array.isArray(rawImages) || rawImages.length < 1 || rawImages.length > 9)
            return '发送图片失败：images 数量必须为 1-9。'

          const images = rawImages.map((item) => {
            const image = item as { description?: unknown } | null
            if (image && typeof image === 'object' && ('url' in image || 'image_url' in image))
              return { description: '' }
            const description = typeof image?.description === 'string' ? image.description.trim() : ''
            return { description }
          })
          if (images.some(image => !image.description || image.description.length > 2000))
            return '发送图片失败：每张图片都必须提供 1-2000 字符的 description。'

          await dispatchMessages([{
            type: 'simulated_image',
            text: '',
            simulatedImages: images,
          }])
          return `已发送 ${images.length} 张模拟图片。`
        }
        catch (error) {
          console.error('[send_images] 图片分发失败:', error)
          return `发送图片失败：${error instanceof Error ? error.message : String(error)}`
        }
      },
    },
    {
      function: {
        name: 'initiate_video_call',
        description: '主动发起视频通话，greeting 是接通后说的第一句话。',
        parameters: SCHEMA_INITIATE_VIDEO_CALL,
      },
      isTerminal: true,
      execute: async (args): Promise<string> => {
        const { greeting } = args as { greeting: string }
        try {
          ctx.onCallInitiate('video', greeting)
        }
        catch (error) {
          console.warn('[initiate_video_call] 发起通话失败:', error)
        }
        return ''
      },
    },
    {
      function: {
        name: 'hang_up_call',
        description: '挂断当前通话。仅在通话进行中使用。',
        parameters: SCHEMA_HANG_UP_CALL,
      },
      isTerminal: true,
      execute: async (args): Promise<string> => {
        const { reason } = (args ?? {}) as { reason?: string }
        try {
          ctx.onHangUp(reason)
        }
        catch (error) {
          console.warn('[hang_up_call] 挂断失败:', error)
        }
        return ''
      },
    },
    {
      function: {
        name: 'speak_in_call',
        description: '通话进行中说话。dialogue 是你要说的话；视频通话可用 description 描述表情动作。仅在通话中使用。',
        parameters: SCHEMA_SPEAK_IN_CALL,
      },
      isTerminal: true,
      execute: async (args): Promise<string> => {
        const { dialogue, description } = args as { dialogue: string, description?: string }
        try {
          ctx.onSpeakInCall(dialogue, description)
        }
        catch (error) {
          console.warn('[speak_in_call] 说话失败:', error)
        }
        return ''
      },
    },
    {
      function: {
        name: 'answer_call',
        description: '回应用户的来电请求。decision=accept 接听（message 是接通第一句话）/reject 拒绝（message 是拒绝理由）。仅在有来电等待接听时使用。',
        parameters: SCHEMA_ANSWER_CALL,
      },
      isTerminal: true,
      execute: async (args): Promise<string> => {
        const { decision, message } = args as { decision: 'accept' | 'reject', message: string }
        try {
          ctx.onAnswerCall(decision, message)
        }
        catch (error) {
          console.warn('[answer_call] 接听失败:', error)
        }
        return ''
      },
    },

    // ----------------------------------------------------------
    // 7. 关系管理
    // ----------------------------------------------------------
    {
      function: {
        name: 'block_user',
        description: '拉黑用户，终止对话。reason 是内部原因，不会发给用户。',
        parameters: SCHEMA_BLOCK_USER,
      },
      isTerminal: true,
      execute: async (args): Promise<string> => {
        const { reason } = args as { reason: string }
        try {
          ctx.onBlockUser(reason)
        }
        catch (error) {
          console.warn('[block_user] 拉黑失败:', error)
        }
        return ''
      },
    },
    {
      function: {
        name: 'friend_request_response',
        description: '回应用户的好友申请。message 是直接发送给用户的话。',
        parameters: SCHEMA_FRIEND_REQUEST_RESPONSE,
      },
      isTerminal: true,
      execute: async (args): Promise<string> => {
        const { decision, message } = args as { decision: 'accept' | 'reject', message: string }
        try {
          ctx.onFriendRequestResponse(decision, message)
        }
        catch (error) {
          console.warn('[friend_request_response] 回应失败:', error)
        }
        return ''
      },
    },
    {
      function: {
        name: 'update_relationship',
        description: '调整与用户的关系好感度。每轮对话可调用一次，score_change 范围 -10~10。',
        parameters: SCHEMA_UPDATE_RELATIONSHIP,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { score_change, reason, new_type } = (args ?? {}) as {
          score_change?: unknown
          reason?: unknown
          new_type?: string
        }
        // 入参守卫：与 send_messages.relationship 分支（上方 dispatchMessages 后）保持一致，
        // score_change 非 number 时静默跳过，防止上游/网关给出畸形参数时误写关系分。
        if (typeof score_change !== 'number') {
          console.warn('[update_relationship] score_change 非数值，已跳过:', score_change)
          return '关系调整失败：score_change 必须为数字。'
        }
        if (!Number.isFinite(score_change) || score_change < -10 || score_change > 10 || typeof reason !== 'string' || !reason.trim() || reason.trim().length > 500 || (new_type !== undefined && (typeof new_type !== 'string' || new_type.trim().length > 20)))
          return '关系调整失败：score_change 必须在 -10 到 10，reason 必须为 1-500 字符。'
        const success = await applyRelationshipOnce({ score_change, reason: reason.trim(), new_type: typeof new_type === 'string' ? new_type.trim() : undefined })
        return success ? '已更新关系。' : '关系调整失败：本轮关系调整已执行过或写入失败。'
      },
    },
    {
      function: {
        name: 'get_relationship_history',
        description: '查询与角色的关系历史和好感度变化曲线。可以了解关系发展过程和关键事件。',
        parameters: SCHEMA_GET_RELATIONSHIP_HISTORY,
      },
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { characterId, eventLimit = 10 } = (args ?? {}) as { characterId?: unknown, eventLimit?: unknown }
        if ((characterId != null && (typeof characterId !== 'string' || !characterId.trim())) || typeof eventLimit !== 'number' || !Number.isInteger(eventLimit) || eventLimit < 1 || eventLimit > 50)
          return '关系历史查询失败：eventLimit 必须为 1-50 的整数，characterId 必须为文本。'
        try {
          const { relationshipStore } = postProcessCtx

          // 确定要查询的角色 ID
          const queryCharacterId = (characterId as string | undefined)?.trim() || targetContactId
          if (!queryCharacterId) {
            return '无法确定要查询的角色。'
          }

          // 获取关系数据
          const relationship = relationshipStore.getRelationship(queryCharacterId)
          if (!relationship) {
            return `与该角色还没有建立关系记录。`
          }

          // 获取关系统计
          const stats = relationshipStore.getRelationshipStats(queryCharacterId)

          // 格式化关系类型
          const typeDescriptions: Record<string, string> = {
            stranger: '陌生人',
            acquaintance: '认识',
            friend: '朋友',
            close_friend: '好友',
            best_friend: '挚友',
            lover: '恋人',
            family: '家人',
            rival: '对手',
          }
          const typeLabel = typeDescriptions[relationship.type] || relationship.type

          // 构建基本信息
          let result = `与 ${relationship.characterName} 的关系信息:\n\n`
          result += `【当前状态】\n`
          result += `- 关系类型: ${typeLabel}\n`
          result += `- 好感度: ${relationship.affection}/100\n`
          result += `- 互动次数: ${relationship.interactionCount}\n`
          result += `- 建立时间: ${new Date(relationship.establishedAt).toLocaleString('zh-CN')}\n`
          result += `- 最后互动: ${new Date(relationship.lastInteractionAt).toLocaleString('zh-CN')}\n`

          if (relationship.tags.length > 0) {
            result += `- 特殊标签: ${relationship.tags.join(', ')}\n`
          }

          // 添加统计信息
          if (stats) {
            result += `\n【统计数据】\n`
            result += `- 历史最高好感度: ${stats.peakAffection}\n`
            result += `- 历史最低好感度: ${stats.lowestAffection}\n`
            result += `- 正面事件: ${stats.positiveEvents}次\n`
            result += `- 负面事件: ${stats.negativeEvents}次\n`
          }

          // 添加历史事件
          if (relationship.history.length > 0) {
            const recentEvents = relationship.history
              .slice(-Math.min(eventLimit, 50))
              .reverse()

            result += `\n【最近${recentEvents.length}条历史事件】\n`
            recentEvents.forEach((event: RelationshipEvent, index: number) => {
              const date = new Date(event.timestamp).toLocaleString('zh-CN')
              const change = event.scoreChange > 0 ? `+${event.scoreChange}` : event.scoreChange
              result += `${index + 1}. [${date}] ${change}分 - ${event.reason}\n`
              if (event.messageContent) {
                result += `   相关消息: ${event.messageContent.substring(0, 50)}${event.messageContent.length > 50 ? '...' : ''}\n`
              }
            })
          }
          else {
            result += `\n【历史事件】\n暂无历史记录\n`
          }

          return result
        }
        catch (error) {
          console.warn('[get_relationship_history] 获取关系历史失败:', error)
          return '获取关系历史失败'
        }
      },
    },

    // ----------------------------------------------------------
    // 8. 插件
    // ----------------------------------------------------------
    {
      function: {
        name: 'plugin_action',
        description: '执行本地 Vue 插件的动作（仅用于浏览器内运行的插件，不是 MCP 服务器）。pluginId 和 action 必须与当前启用的插件匹配。如果要使用外部服务（如滴答清单、搜索等），请使用 call_mcp_tool 而不是此工具。',
        parameters: SCHEMA_PLUGIN_ACTION,
      },
      lifecycle: 'action',
      isTerminal: false,
      execute: async (args): Promise<string> => {
        const { pluginId, action, params } = (args ?? {}) as {
          pluginId?: unknown
          action?: unknown
          params?: Record<string, unknown>
        }
        if (typeof pluginId !== 'string' || !pluginId.trim() || typeof action !== 'string' || !action.trim() || (params != null && (typeof params !== 'object' || Array.isArray(params))))
          return '插件动作失败：pluginId、action 或 params 参数无效。'
        const success = await handlePluginActions(
          { text: '', pluginActions: [{ pluginId, action, params: params ?? {} }] },
          postProcessCtx,
        )
        return success ? '已执行插件动作。' : '插件动作失败：插件执行未成功。'
      },
    },

    // ----------------------------------------------------------
    // 9. 群聊（仅群聊会话下注入；senderId 枚举由成员列表动态生成）
    // ----------------------------------------------------------
    ...(dispatchGroupMessages && groupMembers && groupMembers.length > 0
      ? [buildGroupChatResponseTool(dispatchGroupMessages, groupMembers)]
      : []),
  ]
}

/**
 * 构建群聊消息工具（替代旧的 JSON 数组格式）。
 *
 * senderId 的 enum 由当前群成员动态生成，模型无法再编造不存在的成员 ID——
 * 这是相对旧 JSON 格式最大的收益（旧格式只能靠提示词约束 + 运行时校验兜底）。
 */
function buildGroupChatResponseTool(
  dispatchGroupMessages: NonNullable<ChatToolContext['dispatchGroupMessages']>,
  groupMembers: NonNullable<ChatToolContext['groupMembers']>,
): ChatActionTool {
  const memberRoster = groupMembers.map(m => `${m.id}=${m.name}`).join('、')
  return {
    function: {
      name: 'group_chat_response',
      description: `在群聊中以一个或多个角色的身份发言。每条消息独立发出，之间有自然停顿。当前成员：${memberRoster}`,
      parameters: {
        type: 'object',
        properties: {
          messages: {
            type: 'array',
            description: '本轮要发出的群聊消息列表，按发言顺序排列。不必让所有角色都发言，1-3 个最相关的角色即可。',
            items: {
              type: 'object',
              properties: {
                senderId: {
                  type: 'string',
                  enum: groupMembers.map(m => m.id),
                  description: `发言角色的 ID。可选值：${memberRoster}`,
                },
                content: { type: 'string', description: '发言内容；type 为 sticker/pat 时可留空' },
                type: {
                  type: 'string',
                  enum: ['text', 'sticker', 'pat', 'reply'],
                  description: 'text=普通文字（默认）/sticker=表情包/pat=拍一拍/reply=引用回复',
                },
                name: { type: 'string', description: '表情包名称（type=sticker 时必填，须与可用表情精确匹配）' },
                replyTo: { type: 'string', description: '被回复者的昵称（type=reply 时必填）' },
                replyContent: { type: 'string', description: '被回复消息的摘要（type=reply 时必填）' },
              },
              required: ['senderId'],
            },
            minItems: 1,
          },
        },
        required: ['messages'],
      },
    },
    lifecycle: 'action',
    isTerminal: false,
    execute: async (args): Promise<string> => {
      try {
        const { messages } = (args ?? {}) as { messages?: GroupChatMessageItem[] }
        if (!Array.isArray(messages) || messages.length === 0) {
          console.warn('[group_chat_response] 消息列表为空，已跳过')
          return '群聊回复失败：messages 必须为非空数组。'
        }
        // senderId 白名单校验：enum 只是给模型的提示，网关/兼容路径仍可能给出越界值
        const validIds = new Set(groupMembers.map(m => m.id))
        const accepted = messages.filter((msg) => {
          if (validIds.has(msg.senderId))
            return true
          console.warn(`[group_chat_response] 丢弃非群成员的发言 senderId=${msg.senderId}`)
          return false
        })
        if (accepted.length === 0) {
          console.warn('[group_chat_response] 所有 senderId 均非法，本轮无消息可发')
          return '群聊回复失败：没有合法的群成员 senderId。'
        }
        await dispatchGroupMessages(accepted)
        return `已发送 ${accepted.length} 条群聊消息。`
      }
      catch (error) {
        console.error('[group_chat_response] 群聊消息分发失败:', error)
        return `群聊回复失败：${error instanceof Error ? error.message : String(error)}`
      }
    },
  }
}

// ============================================================
// 朋友圈后台模拟专用工具集（统一实现）
// ============================================================

/**
 * 提取文本内容（兼容 string 或 {type, text} 结构）
 */
function extractTextContent(content: unknown): string {
  if (typeof content === 'string')
    return content
  if (typeof content === 'object' && content !== null) {
    const obj = content as Record<string, unknown>
    if (typeof obj.text === 'string')
      return obj.text
  }
  return String(content)
}

/**
 * 解析 mention：AI 可能返回名字而非 ID，需要转换
 */
function normalizeMentionIds(
  mentionIds: string[] | undefined,
  allContactIds: string[],
  allContactNames: string[],
  userNickname: string,
  contactName: string,
): string[] {
  if (!mentionIds || mentionIds.length === 0)
    return []
  return mentionIds
    .map((mention: string) => {
      // 已经是有效 ID
      if (mention === 'user' || allContactIds.includes(mention))
        return mention
      // 用户昵称
      if (mention === userNickname)
        return 'user'
      // 联系人名字 → ID
      const idx = allContactNames.indexOf(mention)
      if (idx !== -1)
        return allContactIds[idx]
      console.warn(`朋友圈模拟(${contactName})：无法识别的 mention "${mention}"，已忽略`)
      return null
    })
    .filter(Boolean) as string[]
}

const SCHEMA_CREATE_MOMENTS_POST: Record<string, unknown> = {
  type: 'object',
  properties: {
    postType: { type: 'string', enum: ['text', 'image'], description: '动态类型：text 纯文字 / image 图文' },
    content: { type: 'string', minLength: 1, maxLength: 2000, description: '动态正文内容' },
    imageDescription: { type: 'string', minLength: 1, maxLength: 2000, description: '图片描述（postType=image 时必填，用第三人称描述画面内容）' },
    mentionIds: {
      type: 'array',
      maxItems: 20,
      items: { type: 'string', minLength: 1 },
      description: '要 @ 的人（可选）：填写联系人名字或 ID，或填"user"表示用户；系统会自动转换为正确的 ID',
    },
  },
  required: ['postType', 'content'],
}

const SCHEMA_LIKE_MOMENTS_POST: Record<string, unknown> = {
  type: 'object',
  properties: {
    postId: { type: 'string', description: '要点赞的动态 ID（必须是上下文中见到的真实 ID）' },
  },
  required: ['postId'],
}

const SCHEMA_COMMENT_ON_MOMENTS_POST: Record<string, unknown> = {
  type: 'object',
  properties: {
    postId: { type: 'string', description: '要评论的动态 ID（必须是上下文中见到的真实 ID）' },
    commentText: { type: 'string', minLength: 1, maxLength: 2000, description: '评论内容' },
  },
  required: ['postId', 'commentText'],
}

const SCHEMA_DO_NOTHING_MOMENTS: Record<string, unknown> = {
  type: 'object',
  properties: {},
  description: '不执行任何朋友圈动作，保持沉默',
}

/**
 * 朋友圈后台模拟工具上下文（统一版本）
 */
export interface MomentsSimulationToolContext {
  contactId: string
  contactName: string
  contactAvatar?: string
  postsStore: {
    createPost: (params: {
      authorId: string
      authorName: string
      authorAvatar?: string
      type: 'text' | 'image'
      content: string
      imageDescription?: string
      mentionIds?: string[]
      timestamp?: number
    }) => void
    aiLikePost: (postId: string, aiId: string, aiName: string) => boolean
    aiCommentOnPost: (postId: string, commentText: string, aiId: string, aiName: string, replyTo?: { id: string, author: string, authorId: string }) => unknown
  }
  allContactNames: string[]
  allContactIds: string[]
  userNickname: string
  backdatedTimestamp?: number
  onActionExecuted?: (actionType: 'create_post' | 'like_post' | 'comment_on_post' | 'do_nothing') => void
}

/**
 * 构建朋友圈后台模拟专用工具集（统一实现）
 *
 * 与 buildChatActionTools 的区别：
 * - 只包含朋友圈相关的工具（create_moments_post, like_moments_post, comment_on_moments_post, do_nothing_moments）
 * - 不包含聊天消息工具（send_messages, silent, create_memory 等）
 * - 用于后台模拟场景，AI 可以一次调用多个工具（并行执行）
 */
export function buildMomentsSimulationTools(context: MomentsSimulationToolContext): ChatActionTool[] {
  const {
    contactId,
    contactName,
    contactAvatar,
    postsStore,
    allContactNames,
    allContactIds,
    userNickname,
    backdatedTimestamp,
    onActionExecuted,
  } = context

  return [
    {
      function: {
        name: 'create_moments_post',
        description: '发布一条朋友圈动态。可以发布文字或图片动态（图片用文字描述）。',
        parameters: SCHEMA_CREATE_MOMENTS_POST,
      },
      lifecycle: 'terminal',
      isTerminal: true,
      execute: async (args): Promise<string> => {
        try {
          const { postType, content, imageDescription, mentionIds } = (args ?? {}) as {
            postType?: unknown
            content: unknown
            imageDescription?: unknown
            mentionIds?: unknown
          }
          if ((postType !== 'text' && postType !== 'image') || typeof content !== 'string' || !content.trim() || content.trim().length > 2000)
            return '发布动态失败：postType 和 content 参数无效。'
          if (postType === 'image' && (typeof imageDescription !== 'string' || !imageDescription.trim() || imageDescription.trim().length > 2000))
            return '发布动态失败：图片动态必须提供 1-2000 字符的 imageDescription。'
          if (mentionIds !== undefined && (!Array.isArray(mentionIds) || mentionIds.length > 20 || mentionIds.some(mention => typeof mention !== 'string' || !mention.trim())))
            return '发布动态失败：mentionIds 必须是最多 20 个非空文本。'
          const textContent = extractTextContent(content)
          const normalizedMentionIds = normalizeMentionIds(mentionIds as string[] | undefined, allContactIds, allContactNames, userNickname, contactName)

          console.info(`📝 ${contactName} 发布动态: ${textContent.substring(0, 50)}...`)

          postsStore.createPost({
            authorId: contactId,
            authorName: contactName,
            authorAvatar: contactAvatar,
            type: postType === 'image' ? 'image' : 'text',
            content: textContent,
            ...(typeof imageDescription === 'string' && imageDescription.trim() ? { imageDescription: imageDescription.trim() } : {}),
            mentionIds: normalizedMentionIds,
            ...(backdatedTimestamp && { timestamp: backdatedTimestamp }),
          })

          onActionExecuted?.('create_post')
          return '已发布朋友圈动态。'
        }
        catch (error) {
          console.error(`[create_moments_post] ${contactName} 发布失败:`, error)
          return `发布动态失败：${error instanceof Error ? error.message : String(error)}`
        }
      },
    },
    {
      function: {
        name: 'like_moments_post',
        description: '点赞一条朋友圈动态。postId 必须是上下文中见到的真实 ID。',
        parameters: SCHEMA_LIKE_MOMENTS_POST,
      },
      lifecycle: 'terminal',
      isTerminal: true,
      execute: async (args): Promise<string> => {
        try {
          const { postId } = (args ?? {}) as { postId?: unknown }
          if (typeof postId !== 'string' || !postId.trim())
            return '点赞失败：postId 必须为非空文本。'
          console.info(`❤️ ${contactName} 点赞动态: ${postId}`)
          if (!postsStore.aiLikePost(postId.trim(), contactId, contactName))
            return '点赞失败：动态不存在或已经点过赞。'
          onActionExecuted?.('like_post')
          return '已点赞。'
        }
        catch (error) {
          console.error(`[like_moments_post] ${contactName} 点赞失败:`, error)
          return `点赞失败：${error instanceof Error ? error.message : String(error)}`
        }
      },
    },
    {
      function: {
        name: 'comment_on_moments_post',
        description: '评论一条朋友圈动态。postId 必须是上下文中见到的真实 ID。',
        parameters: SCHEMA_COMMENT_ON_MOMENTS_POST,
      },
      lifecycle: 'terminal',
      isTerminal: true,
      execute: async (args): Promise<string> => {
        try {
          const { postId, commentText } = (args ?? {}) as { postId?: unknown, commentText?: unknown }
          if (typeof postId !== 'string' || !postId.trim() || typeof commentText !== 'string' || !commentText.trim() || commentText.trim().length > 2000)
            return '评论失败：postId 必须为非空文本，commentText 必须为 1-2000 字符。'
          console.info(`💬 ${contactName} 评论动态: ${postId}`)
          if (!postsStore.aiCommentOnPost(postId.trim(), commentText.trim(), contactId, contactName))
            return '评论失败：动态不存在。'
          onActionExecuted?.('comment_on_post')
          return '已发表评论。'
        }
        catch (error) {
          console.error(`[comment_on_moments_post] ${contactName} 评论失败:`, error)
          return `评论失败：${error instanceof Error ? error.message : String(error)}`
        }
      },
    },
    {
      function: {
        name: 'do_nothing_moments',
        description: '不执行任何朋友圈动作，保持沉默。当你觉得现在不适合发动态/点赞/评论时使用。',
        parameters: SCHEMA_DO_NOTHING_MOMENTS,
      },
      lifecycle: 'terminal',
      isTerminal: true,
      execute: async (): Promise<string> => {
        console.info(`🤐 ${contactName} 选择保持沉默`)
        onActionExecuted?.('do_nothing')
        return ''
      },
    },
  ]
}

// ============================================================
// 记忆摘要专用工具
// ============================================================

/**
 * 记忆摘要工具的 Schema
 */
const SCHEMA_CONVERSATION_SUMMARY: Record<string, unknown> = {
  type: 'object',
  properties: {
    summary: {
      type: 'string',
      description: '第三人称视角的对话摘要（50-100字），例如："用户询问了关于...，AI回答了..."',
    },
    keywords: {
      type: 'array',
      items: { type: 'string' },
      description: '3-5个最重要的关键词，用于触发回忆',
    },
    importance: {
      type: 'number',
      description: '重要性评分：0-2=普通对话（日常闲聊），3-4=重要对话（有价值的信息交流），5=核心对话（涉及重要承诺、决定、情感转折点）',
      minimum: 0,
      maximum: 5,
    },
    memoryOperations: {
      type: 'array',
      description: '可选的长期记忆变更。只记录稳定偏好、重要事件、承诺、关系转折和未完成话题。',
      items: {
        type: 'object',
        properties: {
          action: { type: 'string', enum: ['add', 'supersede', 'forget'] },
          category: { type: 'string', enum: ['preference', 'fact', 'event', 'promise', 'relationship', 'open_loop'] },
          content: { type: 'string', description: '新增或替代后的记忆内容' },
          match: { type: 'string', description: 'supersede/forget 时用于匹配旧记忆的独特短语' },
          keywords: { type: 'array', items: { type: 'string' } },
          importance: { type: 'number', minimum: 0, maximum: 5 },
          confidence: { type: 'number', minimum: 0, maximum: 1 },
          emotion: { type: 'string' },
        },
        required: ['action'],
      },
    },
    openLoops: {
      type: 'array',
      description: '可选的下一次对话值得自然跟进的未完成事项',
      items: {
        type: 'object',
        properties: {
          content: { type: 'string' },
          keywords: { type: 'array', items: { type: 'string' } },
          importance: { type: 'number', minimum: 0, maximum: 5 },
          confidence: { type: 'number', minimum: 0, maximum: 1 },
        },
        required: ['content'],
      },
    },
    emotionalContext: {
      type: 'string',
      description: '可选的关系或情绪变化摘要，必须基于对话事实',
    },
  },
  required: ['summary', 'keywords', 'importance'],
}

/**
 * 记忆摘要结果
 */
export interface ConversationSummaryResult {
  summary: string
  keywords: string[]
  importance: number
}

/**
 * 构建记忆摘要专用工具
 *
 * 这个工具用于后台自动生成对话摘要，不在对话中使用。
 * AI 调用此工具来返回结构化的摘要数据。
 */
export function buildConversationSummaryTool(): ChatActionTool {
  return {
    function: {
      name: 'submit_conversation_summary',
      description: '提交对话摘要。使用第三人称视角总结对话核心内容，并提取关键词。',
      parameters: SCHEMA_CONVERSATION_SUMMARY,
    },
    isTerminal: true,
    execute: async (_args): Promise<string> => {
      // 工具执行不做任何操作，只是作为结构化返回的载体
      // 实际的摘要数据会在调用方通过 tool-call 事件获取
      return ''
    },
  }
}
